import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Briefcase, MapPin, Clock, DollarSign, Bookmark, BookmarkCheck, Building2, TrendingUp } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const Opportunities = () => {
  const { token, user } = useContext(AuthContext);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [filteredOpportunities, setFilteredOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [filters, setFilters] = useState({
    field: "all",
    work_mode: "all",
    experience_level: "all"
  });

  useEffect(() => {
    fetchOpportunities();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [activeTab, filters, opportunities]);

  const fetchOpportunities = async () => {
    try {
      const response = await axios.get(`${API}/opportunities`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOpportunities(response.data);
      setFilteredOpportunities(response.data);
    } catch (error) {
      console.error("Error loading opportunities:", error);
      toast.error("Failed to load opportunities");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = opportunities;

    // Filter by type (tab)
    if (activeTab !== "all") {
      filtered = filtered.filter(opp => opp.type === activeTab);
    }

    // Filter by field
    if (filters.field !== "all") {
      filtered = filtered.filter(opp => opp.field === filters.field);
    }

    // Filter by work mode
    if (filters.work_mode !== "all") {
      filtered = filtered.filter(opp => opp.work_mode === filters.work_mode);
    }

    // Filter by experience level
    if (filters.experience_level !== "all") {
      filtered = filtered.filter(opp => opp.experience_level === filters.experience_level);
    }

    setFilteredOpportunities(filtered);
  };

  const handleSaveOpportunity = async (opportunityId, isSaved) => {
    try {
      if (isSaved) {
        await axios.delete(`${API}/opportunities/${opportunityId}/save`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Opportunity removed from saved");
      } else {
        await axios.post(`${API}/opportunities/${opportunityId}/save`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Opportunity saved successfully");
      }
      fetchOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save opportunity");
    }
  };

  const getWorkModeColor = (mode) => {
    const colors = {
      remote: "bg-green-100 text-green-700",
      onsite: "bg-blue-100 text-blue-700",
      hybrid: "bg-purple-100 text-purple-700"
    };
    return colors[mode] || "bg-gray-100 text-gray-700";
  };

  const getExperienceLevelColor = (level) => {
    const colors = {
      internship: "bg-yellow-100 text-yellow-700",
      entry: "bg-blue-100 text-blue-700",
      mid: "bg-purple-100 text-purple-700",
      senior: "bg-red-100 text-red-700"
    };
    return colors[level] || "bg-gray-100 text-gray-700";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            <Briefcase className="w-10 h-10 text-indigo-500" />
            Internship & Job Board
          </h1>
          <p className="text-gray-600 text-lg">Discover amazing career opportunities</p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="all" className="mb-6" onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">All Opportunities</TabsTrigger>
            <TabsTrigger value="internship">Internships</TabsTrigger>
            <TabsTrigger value="job">Jobs</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Field</label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={filters.field}
                  onChange={(e) => setFilters({...filters, field: e.target.value})}
                >
                  <option value="all">All Fields</option>
                  <option value="Engineering">Engineering</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Design">Design</option>
                  <option value="Business">Business</option>
                  <option value="Data Science">Data Science</option>
                  <option value="Finance">Finance</option>
                </select>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Work Mode</label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={filters.work_mode}
                  onChange={(e) => setFilters({...filters, work_mode: e.target.value})}
                >
                  <option value="all">All Modes</option>
                  <option value="remote">Remote</option>
                  <option value="onsite">Onsite</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Experience Level</label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={filters.experience_level}
                  onChange={(e) => setFilters({...filters, experience_level: e.target.value})}
                >
                  <option value="all">All Levels</option>
                  <option value="internship">Internship</option>
                  <option value="entry">Entry Level</option>
                  <option value="mid">Mid Level</option>
                  <option value="senior">Senior Level</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="flex gap-4 mb-6">
          <Button variant="outline" onClick={() => navigate("/my-applications")}>
            My Applications
          </Button>
          <Button variant="outline" onClick={() => navigate("/saved-opportunities")}>
            <Bookmark className="w-4 h-4 mr-2" />
            Saved Opportunities
          </Button>
        </div>

        {/* Opportunities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredOpportunities.map((opportunity) => (
            <Card key={opportunity.id} className="card-hover">
              <CardHeader>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge>{opportunity.field}</Badge>
                      <Badge className={getWorkModeColor(opportunity.work_mode)}>
                        {opportunity.work_mode}
                      </Badge>
                      <Badge className={getExperienceLevelColor(opportunity.experience_level)}>
                        {opportunity.experience_level}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl mb-1">{opportunity.title}</CardTitle>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Building2 className="w-4 h-4" />
                      <span className="font-semibold">{opportunity.company_name}</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSaveOpportunity(opportunity.id, opportunity.is_saved)}
                  >
                    {opportunity.is_saved ? (
                      <BookmarkCheck className="w-5 h-5 text-indigo-500" />
                    ) : (
                      <Bookmark className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600 line-clamp-2">{opportunity.description}</p>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-4 h-4" />
                    <span>{opportunity.location}</span>
                  </div>
                  
                  {opportunity.type === "internship" && opportunity.stipend && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <DollarSign className="w-4 h-4" />
                      <span>Stipend: {opportunity.stipend}</span>
                    </div>
                  )}
                  
                  {opportunity.type === "job" && opportunity.salary && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <DollarSign className="w-4 h-4" />
                      <span>Salary: {opportunity.salary}</span>
                    </div>
                  )}

                  {opportunity.duration && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>Duration: {opportunity.duration}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-gray-600">
                    <TrendingUp className="w-4 h-4" />
                    <span>Deadline: {new Date(opportunity.application_deadline).toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <Button
                    className="w-full"
                    onClick={() => navigate(`/opportunities/${opportunity.id}`)}
                  >
                    View Details & Apply
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredOpportunities.length === 0 && (
          <div className="text-center py-12">
            <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No opportunities found matching your filters.</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Opportunities;
